import {toFilename, splitExtension} from './FileUtil';

/**
 * Prepend BOM to `text` if we think the application opening the file may confuse the encoding.
 *
 * @param {string} text
 * @param {string} filename
 * @returns string - either original `text` or `text` prepended with a BOM.
 */
export function _textWithBOM(text: string, filename: string): string {
  // For now, only add a BOM for *.csv files, as Excel will otherwise assume utf-16 encoding.
  if (!/\.csv$/i.test(filename)) {
    return text;
  }
  // All unicode encodings encode ascii codepoints the same, so only add a BOM if there is non-ascii text.
  // eslint-disable-next-line no-control-regex
  if (!/[^\u0000-\u007f]/.test(text)) {
    return text;
  }
  return `\ufeff${text}`;
}

/**
 * Convert text into a utf8-encoded Blob
 */
export function textToBlob(text: string): Blob {
  return new Blob([text], {type: 'text/plain'});
}

/**
 * Convert a string to a data URI that can be used as an href for download,
 * handling Safari accordingly.
 */
export function textToDataURI(text: string): string {
  if (/Safari/i.test(navigator.userAgent)) {
    return `data:text/plain,${encodeURIComponent(text)}`;
  } else {
    const blob = textToBlob(text);
    const createObjectURL =
      // eslint-disable-next-line @typescript-eslint/unbound-method
      (window.webkitURL && window.webkitURL.createObjectURL) || ((b: Blob) => window.URL.createObjectURL(b));
    return createObjectURL(blob);
  }
}

/**
 * Initiate download for the given dataURI
 */
export function initiateFileDownload(filename: string, dataURI: string): void {
  const a = document.createElement('a');
  a.download = filename;
  a.href = dataURI;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Initiate download for the given text, useful in a callback function
 */
export function initiateTextDownload(text: string, filename: string): void {
  const {root, extension} = splitExtension(filename);
  // extension includes the leading dot, so we need to remove it
  const sanitizedFilename = toFilename(root, extension === '' ? '' : extension.slice(1));

  const dataURI = textToDataURI(_textWithBOM(text, filename));
  initiateFileDownload(sanitizedFilename, dataURI);
}
